import { useCallback, useEffect, useState } from "react";
import type { CustomerOrder } from "../types/catalog";

type CustomerOrderResponse = {
  order: CustomerOrder;
};

type UseCustomerOrderOptions = {
  autoRefresh?: boolean;
  orderCode: string;
};

function orderTokenKey(orderCode: string) {
  return `truefit-order-token:${orderCode}`;
}

export function storedOrderAccessToken(orderCode: string) {
  return localStorage.getItem(orderTokenKey(orderCode)) ?? "";
}

export function storeOrderAccessToken(orderCode: string, token: string) {
  localStorage.setItem(orderTokenKey(orderCode), token);
}

export function useCustomerOrder({ autoRefresh = false, orderCode }: UseCustomerOrderOptions) {
  const [order, setOrder] = useState<CustomerOrder | null>(null);
  const [status, setStatus] = useState("");

  const refreshOrder = useCallback(async () => {
    if (!orderCode) {
      setStatus("Order reference is missing.");
      return;
    }

    const token = storedOrderAccessToken(orderCode);

    const headers: HeadersInit = token ? { "X-Order-Access-Token": token } : {};

    const response = await fetch(`/api/orders/${encodeURIComponent(orderCode)}`, {
      credentials: "same-origin",
      headers,
    });

    if (!response.ok) {
      setStatus(
        response.status === 403
          ? "This order needs the original checkout browser or order access token."
          : "Order could not be loaded."
      );
      return;
    }

    const body = await response.json<CustomerOrderResponse>();
    setOrder(body.order);
    setStatus("");
  }, [orderCode]);

  useEffect(() => {
    void refreshOrder();
  }, [refreshOrder]);

  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void refreshOrder();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [autoRefresh, refreshOrder]);

  return {
    order,
    refreshOrder,
    status,
  };
}
